import createDebug from 'debug';
import { authFetch } from './client';

const debug = createDebug('app:api:public-access');
const API_BASE = '/api';

// ============================================
// Types
// ============================================

export interface PublicAccessStatus {
	enabled: boolean;
	token?: string;
	url?: string;
	canRead?: boolean; // Queue only
	canWrite?: boolean; // Queue only
	canDelete?: boolean; // Queue only
	useCount?: number;
	lastUsedAt?: number;
	expiresAt?: number;
	createdAt?: number;
}

export interface CreateQueuePublicAccessInput {
	canRead: boolean;
	canWrite: boolean;
	canDelete: boolean;
	expiresAt?: number;
}

export interface UpdateQueuePublicAccessInput {
	canRead?: boolean;
	canWrite?: boolean;
	canDelete?: boolean;
	expiresAt?: number | null;
}

export interface CreateLinkPublicAccessInput {
	expiresAt?: number;
}

export interface CreatePublicAccessResponse {
	token: string;
	url: string;
	canRead?: boolean;
	canWrite?: boolean;
	isNew?: boolean;
}

// ============================================
// Queue Public Access API
// ============================================

export async function getQueuePublicAccess(queueId: number): Promise<PublicAccessStatus> {
	debug('getQueuePublicAccess: queueId=%d', queueId);
	const response = await authFetch(`${API_BASE}/queues/${queueId}/public`);
	if (!response.ok) {
		const error = await response.text();
		debug('getQueuePublicAccess error: %s', error);
		throw new Error(`Failed to get queue public access: ${error}`);
	}
	return response.json();
}

export async function enableQueuePublicAccess(
	queueId: number,
	options: CreateQueuePublicAccessInput
): Promise<CreatePublicAccessResponse> {
	debug('enableQueuePublicAccess: queueId=%d options=%o', queueId, options);
	const response = await authFetch(`${API_BASE}/queues/${queueId}/public`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(options),
	});
	if (!response.ok) {
		const error = await response.text();
		debug('enableQueuePublicAccess error: %s', error);
		throw new Error(`Failed to enable queue public access: ${error}`);
	}
	return response.json();
}

export async function updateQueuePublicAccess(
	queueId: number,
	updates: UpdateQueuePublicAccessInput
): Promise<void> {
	debug('updateQueuePublicAccess: queueId=%d updates=%o', queueId, updates);
	const response = await authFetch(`${API_BASE}/queues/${queueId}/public`, {
		method: 'PATCH',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(updates),
	});
	if (!response.ok) {
		const error = await response.text();
		debug('updateQueuePublicAccess error: %s', error);
		throw new Error(`Failed to update queue public access: ${error}`);
	}
}

export async function disableQueuePublicAccess(queueId: number): Promise<void> {
	debug('disableQueuePublicAccess: queueId=%d', queueId);
	const response = await authFetch(`${API_BASE}/queues/${queueId}/public`, {
		method: 'DELETE',
	});
	if (!response.ok) {
		const error = await response.text();
		debug('disableQueuePublicAccess error: %s', error);
		throw new Error(`Failed to disable queue public access: ${error}`);
	}
}

// ============================================
// Link Public Access API
// ============================================

export async function getLinkPublicAccess(linkId: number): Promise<PublicAccessStatus> {
	debug('getLinkPublicAccess: linkId=%d', linkId);
	const response = await authFetch(`${API_BASE}/links/${linkId}/public`);
	if (!response.ok) {
		const error = await response.text();
		debug('getLinkPublicAccess error: %s', error);
		throw new Error(`Failed to get link public access: ${error}`);
	}
	return response.json();
}

export async function enableLinkPublicAccess(
	linkId: number,
	options?: CreateLinkPublicAccessInput
): Promise<CreatePublicAccessResponse> {
	debug('enableLinkPublicAccess: linkId=%d options=%o', linkId, options);
	const response = await authFetch(`${API_BASE}/links/${linkId}/public`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(options || {}),
	});
	if (!response.ok) {
		const error = await response.text();
		debug('enableLinkPublicAccess error: %s', error);
		throw new Error(`Failed to enable link public access: ${error}`);
	}
	return response.json();
}

export async function disableLinkPublicAccess(linkId: number): Promise<void> {
	debug('disableLinkPublicAccess: linkId=%d', linkId);
	const response = await authFetch(`${API_BASE}/links/${linkId}/public`, {
		method: 'DELETE',
	});
	if (!response.ok) {
		const error = await response.text();
		debug('disableLinkPublicAccess error: %s', error);
		throw new Error(`Failed to disable link public access: ${error}`);
	}
}
